import { SubscriptionResult as Subscription } from "../lib/bridge";

type Tier = Subscription["tier"];

const TIER_LABEL: Record<Tier, string> = { free: "Free", duo: "Duo", family: "Family" };
const TIER_MEMBERS: Record<Tier, string> = { free: "1 member", duo: "2 members", family: "up to 8 members" };

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

interface CurrentPlanSectionProps {
  sub: Subscription;
  isStripe: boolean;
  isPromo: boolean;
  periodLabel: string | null;
  busy: boolean;
  onToggleAutoRenew: () => void;
}

export function CurrentPlanSection({
  sub,
  isStripe,
  periodLabel,
  busy,
  onToggleAutoRenew,
}: CurrentPlanSectionProps) {
  return (
    <div className="plan-card">
      <div className="plan-title">{TIER_LABEL[sub.tier]} plan</div>
      <div className="plan-subtitle">{TIER_MEMBERS[sub.tier]}</div>
      <div className="plan-usage">
        {sub.memberCount} of {sub.memberLimit} member{sub.memberLimit !== 1 ? "s" : ""} used
      </div>
      {periodLabel && <div className="plan-period-label">{periodLabel}</div>}
      {sub.status === "past_due" && <div className="plan-past-due">Payment past due - please update your card via Stripe.</div>}
      {isStripe && (
        <div className="plan-auto-renew-row">
          <span>Auto-renew: <strong>{sub.autoRenew ? "On" : "Off"}</strong></span>
          <button className="btn-secondary btn-small" disabled={busy} onClick={onToggleAutoRenew}>
            {sub.autoRenew ? "Turn off" : "Turn on"}
          </button>
        </div>
      )}
    </div>
  );
}

interface PlanOptionsSectionProps {
  sub: Subscription;
  isStripe: boolean;
  isPromo: boolean;
  busy: boolean;
  onStartCheckout: (tier: "duo" | "family") => void;
  onSwitchTier: (tier: "duo" | "family") => void;
}

export function PlanOptionsSection({
  sub,
  isStripe,
  isPromo,
  busy,
  onStartCheckout,
  onSwitchTier,
}: PlanOptionsSectionProps) {
  return (
    <>
      <h2 className="plan-options-heading">Plans</h2>
      {(["duo", "family"] as const).map((tier) => {
        const isCurrent = sub.tier === tier && (isStripe || isPromo);
        const price = sub.prices[tier];
        const verb = sub.tier === "free" ? "Upgrade to" : tier === "family" && sub.tier === "duo" ? "Upgrade to" : "Switch to";
        return (
          <div key={tier} className="plan-option-row">
            <div>
              <div className="plan-option-label">{TIER_LABEL[tier]} - ${price.toFixed(2)}/yr</div>
              <div className="plan-option-members">{TIER_MEMBERS[tier]}</div>
            </div>
            {isCurrent ? (
              <span className="plan-option-current">Current</span>
            ) : isPromo ? null : (
              <button
                className="btn-primary btn-small"
                disabled={busy}
                onClick={() => (isStripe ? onSwitchTier(tier) : onStartCheckout(tier))}
              >
                {verb} {TIER_LABEL[tier]}
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

export { formatDate };
